import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CreateProduct } from '../ProductStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { PricingTab } from './Pricing/PricingTab'
import { DiscountsTab } from './DiscountsTab'
import { BarcodeTab } from '../Components/BarcodeTab'
import { Details } from './Details'
import { Cannabinoids } from './Cannabinoids'
import { CustomFieldsTab } from './CustomFieldsTab'
import { ProductTab, ProductTabs } from './ProductTabs'
import { InventoryTab } from './InventoryTab'
import { CannabinoidsBatch } from './CannabinoidsBatch'
import { ProductEventLogs } from '../ProductEventLogs'
import { Tags } from './Tags'

interface Props {
  product: CreateProduct
  currentCompanyStore?: CurrentCompanyStore
  saveProductFirstWarning: JSX.Element
  blockSaving: (blockSaving: boolean, errorMessage?: string) => void
}

interface State {
  tab: ProductTab
  showLogsDrawer: boolean
}

@inject('currentCompanyStore')
@observer
export class DetailsSection extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      tab: ProductTab.PRICING,
      showLogsDrawer: false,
    }
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const { product, saveProductFirstWarning } = this.props

    const isBatchTracked = product.isBatchTracked
    const canUseBatchCannabinoid = this.props.currentCompanyStore?.canUseCannabinoidPerBatch

    const widthBreakPoint = 800

    if (this.props.product.needsPricingGroupRefresh) {
      return (
        <div className='card mb-4'>
          <div className='card-body'>
            <div className='col-12'>
              <h3 className='text-muted'>Please save your product first to edit details.</h3>
            </div>
          </div>
        </div>
      )
    }

    return (
      !product.isMasterProduct && (
        <div className='card mb-4'>
          <div className='card-header d-flex'>
            <div style={{ flexGrow: 1 }}>Product details</div>
          </div>

          <div className='card-body'>
            <ProductTabs
              tabClassName='mt-3'
              tabs={[
                {
                  tab: ProductTab.PRICING,
                  content: (
                    <PricingTab
                      product={product}
                      saveProductFirstWarning={saveProductFirstWarning}
                    />
                  ),
                },
                {
                  tab: ProductTab.DISCOUNTS,
                  content: <DiscountsTab product={product} />,
                },
                {
                  tab: ProductTab.BARCODE,
                  content: <BarcodeTab product={product} />,
                },
                {
                  tab: ProductTab.DETAILS,
                  content: <Details product={product} />,
                },
                {
                  tab: ProductTab.CANNABINOIDS,
                  content: <Cannabinoids product={product} />,
                  isVisible:
                    canUseBatchCannabinoid === false ||
                    (canUseBatchCannabinoid === true && !isBatchTracked),
                },
                {
                  tab: ProductTab.CANNABINOIDS_BATCH,
                  content: <CannabinoidsBatch product={product} />,
                  isVisible: canUseBatchCannabinoid === true && isBatchTracked,
                },
                {
                  tab: ProductTab.TAGS,
                  content: (
                    <Tags
                      tags={this.props.currentCompanyStore?.tags ?? []}
                      companyId={company.id}
                      product={product}
                    />
                  ),
                },
                {
                  tab: ProductTab.CUSTOM_FIELDS,
                  content: (
                    <CustomFieldsTab blockSaving={this.props.blockSaving} product={product} />
                  ),
                },
                {
                  tab: ProductTab.INVENTORY,
                  isVisible: product.trackInventory,
                  content: <InventoryTab product={product} />,
                },
                {
                  tab: ProductTab.EDIT_HISTORY,
                  isVisible: !!product.id,
                  content: <ProductEventLogs product={product} />,
                },
              ]}
            />
          </div>
        </div>
      )
    )
  }
}
